import { evaluateDocMath } from './mathExpr';

/**
 * Stored math in a variable value: `{{= 2 + 1 }}` or `{{= {{qty}} * 2 }}`.
 * The `=` after `{{` is what distinguishes it from a variable ref.
 */

export const wrapMathValueToken = (expr) => {
  const formula = String(expr ?? '').trim();
  return formula ? `{{= ${formula} }}` : '';
};

const findClosingBraces = (source, innerStart) => {
  let depth = 0;
  for (let i = innerStart; i < source.length - 1; i += 1) {
    if (source[i] === '{' && source[i + 1] === '{') {
      depth += 1;
      i += 1;
      continue;
    }
    if (source[i] === '}' && source[i + 1] === '}') {
      if (depth === 0) return i;
      depth -= 1;
      i += 1;
    }
  }
  return -1;
};

/** Walk `{{= … }}` tokens. Inner `{{vars}}` are allowed. */
export const forEachMathValueToken = (raw, onToken) => {
  const source = String(raw ?? '');
  let i = 0;
  while (i < source.length) {
    const open = source.indexOf('{{=', i);
    if (open === -1) break;
    const close = findClosingBraces(source, open + 3);
    if (close === -1) break;
    const expr = source.slice(open + 3, close).trim();
    onToken({
      expr,
      token: source.slice(open, close + 2),
      start: open,
      end: close + 2,
    });
    i = close + 2;
  }
};

export const containsMathValueToken = (raw) => {
  let found = false;
  forEachMathValueToken(raw, () => {
    found = true;
  });
  return found;
};

/** Replace each `{{= expr }}` with the callback result. */
export const replaceMathValueTokens = (raw, replace) => {
  const source = String(raw ?? '');
  if (!source.includes('{{=')) return source;
  let out = '';
  let last = 0;
  forEachMathValueToken(source, ({ expr, token, start, end }) => {
    out += source.slice(last, start);
    out += replace(expr, token);
    last = end;
  });
  out += source.slice(last);
  return out;
};

/**
 * Evaluate stored math tokens to their current result (for {{name}} expand).
 * A formula that cannot calculate shows its unfulfilled {{variables}}, not
 * the raw token — otherwise empty refs are stripped and only + − = remain.
 */
export const expandMathTokensInValue = (raw, resolveVariable) =>
  replaceMathValueTokens(raw, (expr, token) => {
    const result = evaluateDocMath(expr, resolveVariable);
    if (result.ok) return result.text;
    return result.text && result.text !== expr ? result.text : token;
  });
